import { createContext, useContext, useEffect, useState } from "react";

import {
  getApplicationConfig,
  resetApplicationConfigCache,
} from "app/utils/configs";

import { ConfigSchema, type AppConfig } from "./schema";

import type { z } from "zod";

export type { AppConfig };

interface Props {
  children: React.ReactNode;
}

const ConfigContext = createContext<AppConfig | null>(null);

/**
 * Exposed for test isolation only — resets the cached `application.json`.
 *
 * A function rather than a re-export, so that a test which partially mocks
 * `app/utils/configs` is not required to provide this one.
 */
export function resetConfigCache() {
  resetApplicationConfigCache();
}

/**
 * Reads this deployment's flags from `application.json`.
 *
 * The same file carries a block per service, which `app/utils/configs` reads
 * from the same shared promise — so the console fetches it once however many
 * readers it has. Only the flags come back here; `ConfigSchema` declares them,
 * and the platform allow-lists exactly that list before serving it.
 */
export async function fetchConfig(): Promise<AppConfig> {
  const config = await getApplicationConfig();
  // A development aid only: a config that doesn't match still loads, so
  // production builds neither check it nor log about it.
  if (import.meta.env.DEV) {
    try {
      ConfigSchema.parse(config);
    } catch (err) {
      const messages = (err as z.ZodError).issues
        .map((issue) => `\n  * ${issue.path}: ${issue.message}`)
        .join("");
      console.error("Unable to parse config", config, messages);
    }
  }

  return config;
}

export function ConfigProvider({ children }: Props) {
  const [config, setConfig] = useState<AppConfig | null>(null);

  useEffect(() => {
    const getConfig = async () => {
      const config = await fetchConfig().catch((error: Error) =>
        console.error(error),
      );
      if (config) {
        setConfig(config);
      }
    };

    getConfig();
  }, []);

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
}

export const useConfig = () => {
  return useContext(ConfigContext);
};
